import React from 'react';
import PropTypes from 'prop-types';
import { faArrowLeft, faCheck } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { WizardFieldGroup, WizardInput, WizardSelect } from './WizardField';
import { JOB_ROLE_OPTIONS, HEAR_ABOUT_OPTIONS } from '../utils/constants';
import { useTranslation } from 'react-i18next';

/**
 * Step 3: Team & Background — modern Radix selects with conditional
 * "Other" follow-up inputs. All fields are optional except the conditional
 * "specify your role" follow-up.
 */
const WizardStep3 = ({ formData, updateField, onSubmit, onBack, isSubmitting, errors }) => {
  const { t } = useTranslation('onboarding');
  const showOtherJobRole = formData.userJobRole === 'Other';
  const showOtherHearAboutUs = formData.hearAboutUs === 'Other';

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-800 m-0">
          {t('wizard.teamBackgroundTitle', 'Team & background')}
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          {t('wizard.teamBackgroundHint', 'Last bit — these help us shape the welcome experience.')}
        </p>
      </div>

      {errors?.general ? (
        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-4">
          <p className="text-sm text-red-700 m-0">{errors.general}</p>
        </div>
      ) : null}

      <WizardFieldGroup
        id="userJobRole"
        label={t('wizard.positionQuestion', 'What best describes your position in the company?')}
      >
        <WizardSelect
          id="userJobRole"
          value={formData.userJobRole}
          onChange={(v) => updateField('userJobRole', v)}
          options={JOB_ROLE_OPTIONS}
          placeholder={t('wizard.selectRole', 'Select your role')}
        />
      </WizardFieldGroup>

      {showOtherJobRole ? (
        <WizardFieldGroup
          id="otherJobRole"
          label={t('wizard.specifyRole', 'Please specify your role')}
          required
          error={errors?.otherJobRole}
        >
          <WizardInput
            id="otherJobRole"
            value={formData.otherJobRole}
            onChange={(v) => updateField('otherJobRole', v)}
            error={errors?.otherJobRole}
            placeholder={t('wizard.enterRole', 'Enter your role')}
            maxLength={100}
          />
        </WizardFieldGroup>
      ) : null}

      <WizardFieldGroup
        id="hearAboutUs"
        label={t('wizard.howDidYouHear', 'How did you hear about us?')}
      >
        <WizardSelect
          id="hearAboutUs"
          value={formData.hearAboutUs}
          onChange={(v) => updateField('hearAboutUs', v)}
          options={HEAR_ABOUT_OPTIONS}
          placeholder={t('wizard.selectSource', 'Select source')}
        />
      </WizardFieldGroup>

      {showOtherHearAboutUs ? (
        <WizardFieldGroup
          id="otherHearAboutUs"
          label={t('wizard.tellUsMore', 'Please tell us more')}
          error={errors?.otherHearAboutUs}
        >
          <WizardInput
            id="otherHearAboutUs"
            value={formData.otherHearAboutUs}
            onChange={(v) => updateField('otherHearAboutUs', v)}
            error={errors?.otherHearAboutUs}
            placeholder={t('wizard.tellUsMoreShort', 'Tell us more')}
            maxLength={200}
          />
        </WizardFieldGroup>
      ) : null}

      <div className="flex justify-between items-center mt-8 pt-4 border-t border-slate-100">
        <CgRadixButton
          variant="ghost"
          size="lg"
          onClick={onBack}
          disabled={isSubmitting}
          icon={faArrowLeft}
        >
          {t('common:actions.back', 'Back')}
        </CgRadixButton>
        <CgRadixButton
          variant="primary"
          size="lg"
          onClick={onSubmit}
          loading={isSubmitting}
          loadingLabel={t('wizard.completing', 'Completing...')}
          icon={faCheck}
        >
          {t('actions.completeSetup', 'Complete setup')}
        </CgRadixButton>
      </div>
    </div>
  );
};

WizardStep3.propTypes = {
  formData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  errors: PropTypes.object,
};

export default WizardStep3;
